import { useScrollReveal } from '../hooks/useScrollReveal';
import { useParallax } from '../hooks/useParallax';

const heatmaps = [
  {
    title: '一师一档',
    subtitle: '教学维度 × 多节课程',
    icon: 'ri-user-line',
    color: 'primary' as const,
    legend: [
      { label: '表现稳定 / 证据充分', tone: 'bg-primary-500' },
      { label: '发展过程中', tone: 'bg-primary-300' },
      { label: '需要进一步支持', tone: 'bg-secondary-200' },
    ],
  },
  {
    title: '一校一图',
    subtitle: '课程主题 × 评价维度',
    icon: 'ri-building-line',
    color: 'accent' as const,
    legend: [
      { label: '表现稳定 / 证据充分', tone: 'bg-accent-500' },
      { label: '发展过程中', tone: 'bg-accent-300' },
      { label: '需要进一步支持', tone: 'bg-secondary-200' },
    ],
  },
  {
    title: '一省一图',
    subtitle: '地区/学校 × 质量维度',
    icon: 'ri-map-2-line',
    color: 'primary' as const,
    legend: [
      { label: '表现稳定 / 证据充分', tone: 'bg-primary-500' },
      { label: '发展过程中', tone: 'bg-primary-300' },
      { label: '需要进一步支持', tone: 'bg-secondary-200' },
    ],
  },
];

export default function P74CourseCoverage({ forceVisible = false }: { forceVisible?: boolean }) {
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal(0.12, forceVisible);
  const { ref: orb1Ref, offset: orb1Y } = useParallax(0.05);
  const { ref: orb2Ref, offset: orb2Y } = useParallax(-0.04);

  return (
    <div className="relative h-screen flex flex-col justify-center overflow-hidden bg-background-50">
      <div ref={orb1Ref} className="absolute top-20 right-24 w-80 h-80 rounded-full bg-primary-100/10 blur-[100px] pointer-events-none" style={{ transform: `translateY(${orb1Y}px)` }} />
      <div ref={orb2Ref} className="absolute bottom-28 left-12 w-96 h-96 rounded-full bg-accent-100/8 blur-[120px] pointer-events-none" style={{ transform: `translateY(${orb2Y}px)` }} />

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 py-6 md:py-8">
        <div ref={titleRef} className="text-center mb-4 md:mb-5">
          <span className={`inline-flex items-center gap-2 text-xs font-body font-semibold text-gold-600 uppercase tracking-[0.15em] mb-4 transition-all duration-700 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <span className="gold-dot" />热力覆盖图
          </span>
          <h2 className={`font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-4 transition-all duration-1000 delay-100 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            连接个体成长、学校质量和<span className="text-gold-600">区域趋势</span>
          </h2>
          <p className={`text-sm text-foreground-600 font-body max-w-2xl mx-auto leading-relaxed transition-all duration-1000 delay-200 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            热力图负责发现位置，专业判断仍然要回到课程标准和课堂证据
          </p>
        </div>

        {/* Three Heatmap Cards */}
        <div className={`grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5 max-w-6xl mx-auto mb-3 transition-all duration-1000 delay-300 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          {heatmaps.map((hm, hmi) => (
            <div
              key={hm.title}
              className="p-5 glass-liquid rounded-xl"
              style={{ transitionDelay: `${300 + hmi * 150}ms` }}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-10 h-10 rounded-xl ${hm.color === 'accent' ? 'bg-gold-100/80 border border-gold-300/40' : 'bg-primary-100/60 border border-primary-200/40'} flex items-center justify-center`}>
                  <i className={`${hm.icon} text-lg ${hm.color === 'accent' ? 'text-gold-600' : 'text-primary-600'}`} />
                </div>
                <div>
                  <h4 className="font-heading text-sm font-bold text-foreground-800">{hm.title}</h4>
                  <p className="text-xs text-foreground-500 font-body">{hm.subtitle}</p>
                </div>
              </div>

              <div className="flex flex-col gap-1.5 mb-4">
                {hm.legend.map((l, li) => (
                  <div key={li} className="flex items-center gap-2">
                    <span className={`w-3 h-3 rounded-sm ${l.tone}`} />
                    <span className="text-xs font-body text-foreground-500">{l.label}</span>
                  </div>
                ))}
              </div>

              {/* Mini heatmap grid */}
              <div className="rounded-lg overflow-hidden">
                <div className="grid grid-cols-5 gap-px bg-background-200/20 p-1">
                  {Array.from({ length: 20 }).map((_, ci) => (
                    <div
                      key={ci}
                      className={`aspect-square rounded-sm ${[hm.color === 'primary' ? 'bg-primary-500' : 'bg-gold-500', hm.color === 'primary' ? 'bg-primary-300' : 'bg-gold-300', 'bg-secondary-200'][ci % 3]}`}
                      style={{ opacity: 0.5 + Math.random() * 0.4 }}
                    />
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className={`max-w-3xl mx-auto text-center transition-all duration-1000 delay-700 ${titleVisible ? 'opacity-100' : 'opacity-0'}`}>
          <div className="p-5 glass-liquid rounded-xl">
            <p className="text-sm text-foreground-600 font-body leading-relaxed">
              热力图不是为了用颜色替代专业判断，而是帮助管理者快速发现需要进一步查看的位置。管理者可以从区域下钻到学校，从学校下钻到课程和教师，<strong className="text-gold-700">最终回到某一节课的具体证据</strong>。
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}